import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from '@mui/material';
import LoteChip, { getLoteChipStatus } from './LoteChip';
import LoteLineaButton from './LoteLineaButton';
import type { LoteAsignacion } from '../../services/loteService';
import {
  productoControlaLote,
  type KitLoteComponenteLinea,
  type LoteMode,
} from '../../utils/loteLineaHelpers';

interface KitLotesLineaButtonProps {
  componentes: KitLoteComponenteLinea[] | null | undefined;
  cantidadKits: number;
  nroUbicacion: number;
  mode: LoteMode;
  onChange: (componentes: KitLoteComponenteLinea[]) => void;
  disabled?: boolean;
  autoOpenSignal?: number | string | null;
  iconOnly?: boolean;
}

const KitLotesLineaButton: React.FC<KitLotesLineaButtonProps> = ({
  componentes,
  cantidadKits,
  nroUbicacion,
  mode,
  onChange,
  disabled,
  autoOpenSignal,
  iconOnly,
}) => {
  const [open, setOpen] = useState(false);
  const lastSignalRef = useRef<number | string | null | undefined>(undefined);
  const kitQty = Number(cantidadKits) || 0;

  const conControl = useMemo(
    () => (componentes ?? []).filter((c) => productoControlaLote(c.controlLoteSerie)),
    [componentes],
  );

  const status = useMemo(() => {
    if (conControl.length === 0) return 'ok' as const;
    for (const c of conControl) {
      const qty = (Number(c.cantidadPorKit) || 0) * kitQty;
      const s = getLoteChipStatus(true, qty, c.lotes);
      if (s !== 'ok') return s;
    }
    return 'ok' as const;
  }, [conControl, kitQty]);

  useEffect(() => {
    if (conControl.length === 0 || disabled || autoOpenSignal == null) return;
    if (autoOpenSignal === lastSignalRef.current) return;
    if (status !== 'incomplete' && status !== 'expired') {
      lastSignalRef.current = autoOpenSignal;
      return;
    }
    if (kitQty <= 0) return;
    lastSignalRef.current = autoOpenSignal;
    setOpen(true);
  }, [autoOpenSignal, conControl.length, disabled, status, kitQty]);

  if (conControl.length === 0) return null;

  const updateComp = (codigo: string, lotes: LoteAsignacion[]) => {
    const next = (componentes ?? []).map((c) =>
      String(c.codigoArticulo).trim().toUpperCase() === codigo.trim().toUpperCase()
        ? { ...c, lotes }
        : c,
    );
    onChange(next);
  };

  return (
    <>
      <LoteChip
        controla
        cantidad={kitQty}
        lotes={status === 'ok' ? [{ cantidad: kitQty } as LoteAsignacion] : []}
        dense
        iconOnly={iconOnly}
        readOnly={disabled}
        onClick={() => setOpen(true)}
      />
      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Lotes de componentes del kit</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            {conControl.map((c) => {
              const qty = (Number(c.cantidadPorKit) || 0) * kitQty;
              return (
                <Box key={c.codigoArticulo}>
                  <Typography variant="body2" fontWeight={600}>
                    {c.codigoArticulo}
                    {c.descripcion ? ` — ${c.descripcion}` : ''}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>
                    Cantidad: {qty}
                  </Typography>
                  <LoteLineaButton
                    controlLoteSerie={c.controlLoteSerie}
                    requiereVencimiento={c.requiereVencimiento}
                    codigoArticulo={c.codigoArticulo}
                    cantidad={qty}
                    nroUbicacion={nroUbicacion}
                    mode={mode}
                    lotes={c.lotes}
                    disabled={disabled}
                    onChange={(lotes) => updateComp(c.codigoArticulo, lotes)}
                  />
                </Box>
              );
            })}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cerrar</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default KitLotesLineaButton;
